"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useDebouncer = exports.usePrevious = exports.useTimer = exports.useDelay = exports.useMountEffect = void 0;

var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));

var _react = require("react");

//
//  toolkit/hooks.tsx
//  Project-Lego
//
//  Created by Sola Ogunsakin on 02/11/20.
//  Copyright © 2020-present ContextLogic Inc. All rights reserved.
//
// For when an effect should only be ran during mount instead of after every render
var useMountEffect = function useMountEffect(fun) {
  return (0, _react.useEffect)(fun, []);
};

exports.useMountEffect = useMountEffect;

var useDelay = function useDelay() {
  var delay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;

  var _useState = (0, _react.useState)(false),
      _useState2 = (0, _slicedToArray2["default"])(_useState, 2),
      canRender = _useState2[0],
      setCanRender = _useState2[1];

  (0, _react.useEffect)(function () {
    if (!delay) {
      return;
    }

    var timeoutId = setTimeout(function () {
      return setCanRender(true);
    }, delay);
    return function () {
      return clearTimeout(timeoutId);
    };
  }, [delay]);
  return !delay ? true : canRender;
};

exports.useDelay = useDelay;

var useTimer = function useTimer(args) {
  var periodMs = args.periodMs,
      _args$intervalMs = args.intervalMs,
      intervalMs = _args$intervalMs === void 0 ? 1000 : _args$intervalMs,
      _args$startNow = args.startNow,
      startNow = _args$startNow === void 0 ? false : _args$startNow;

  var _useState3 = (0, _react.useState)(startNow ? periodMs : 0),
      _useState4 = (0, _slicedToArray2["default"])(_useState3, 2),
      timeLeft = _useState4[0],
      setTimeLeft = _useState4[1];

  (0, _react.useEffect)(function () {
    var intervalID = setInterval(function () {
      if (timeLeft > 0) {
        setTimeLeft(Math.max(timeLeft - intervalMs, 0));
      } else {
        clearInterval(intervalID);
      }
    }, intervalMs);
    return function () {
      return clearInterval(intervalID);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);
  var reset = (0, _react.useCallback)(function () {
    return setTimeLeft(periodMs);
  }, [periodMs]);
  return [timeLeft, reset];
};

exports.useTimer = useTimer;

var usePrevious = function usePrevious(value) {
  var ref = (0, _react.useRef)();
  (0, _react.useEffect)(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
};

exports.usePrevious = usePrevious;

// eslint-disable-next-line local-rules/no-large-method-params
var useDebouncer = function useDebouncer(incoming, debouncePeriodMs, options) {
  var _ref = options || {},
      _ref$returnLoading = _ref.returnLoading,
      returnLoading = _ref$returnLoading === void 0 ? false : _ref$returnLoading;

  var _useState5 = (0, _react.useState)(incoming),
      _useState6 = (0, _slicedToArray2["default"])(_useState5, 2),
      frozenValue = _useState6[0],
      setFrozenValue = _useState6[1];

  var _useState7 = (0, _react.useState)(false),
      _useState8 = (0, _slicedToArray2["default"])(_useState7, 2),
      isLoading = _useState8[0],
      setIsLoading = _useState8[1];

  var _useState9 = (0, _react.useState)(null),
      _useState10 = (0, _slicedToArray2["default"])(_useState9, 2),
      intervalID = _useState10[0],
      setIntervalID = _useState10[1];

  var _useState11 = (0, _react.useState)({
    pendingValue: incoming,
    lastKeyStrokeTime: new Date().getTime()
  }),
      _useState12 = (0, _slicedToArray2["default"])(_useState11, 1),
      bag = _useState12[0];

  (0, _react.useEffect)(function () {
    bag.lastKeyStrokeTime = new Date().getTime();
    bag.pendingValue = incoming;
    setIsLoading(true);
  }, [incoming, bag]);
  (0, _react.useEffect)(function () {
    if (intervalID != null) {
      clearInterval(intervalID);
    }

    var newIntervalID = setInterval(function () {
      var lastKeyStrokeTime = bag.lastKeyStrokeTime,
          pendingValue = bag.pendingValue;
      var now = new Date().getTime();

      if (now - lastKeyStrokeTime >= debouncePeriodMs) {
        setFrozenValue(pendingValue);
        setIsLoading(false);
      }
    }); // @ts-ignore: weird inconsistency between `ttsc` and babel
    // `ttsc` agrees with this expression but babel TS plugin
    // doesn't. Suppress both as the logic is functionally sound.

    setIntervalID(newIntervalID); // Reason: adding `intervalID` to the deps will put us in
    // an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bag, debouncePeriodMs, setFrozenValue, setIntervalID]);

  if (returnLoading) {
    return [frozenValue, isLoading];
  }

  return frozenValue;
};

exports.useDebouncer = useDebouncer;